import React, { useState, useEffect, useCallback } from 'react';

const API = '/api';

function authHeaders() {
  const token = localStorage.getItem('lc_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' };
}

const C = {
  grn: '#3B6D11', grnBg: '#EAF3DE', grnL: '#7AB648',
  amb: '#854F0B', ambBg: '#FAEEDA',
  red: '#A32D2D', redBg: '#FCEBEB',
  acc: '#185FA5', accBg: '#E6F1FB',
  border: 'rgba(0,0,0,0.08)',
  bg: '#F2F0EB', card: '#fff',
  txt: '#1a1a1a', txt2: '#666'
};

function effColor(pct) {
  if (pct >= 75) return C.grn;
  if (pct >= 60) return C.amb;
  return C.red;
}

function effBg(pct) {
  if (pct >= 75) return C.grnBg;
  if (pct >= 60) return C.ambBg;
  return C.redBg;
}

function UnitKpi({ label, value, sub, color }) {
  return (
    <div style={{ background: C.card, borderRadius: 10, padding: '12px 18px', border: `0.5px solid ${C.border}`, flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: C.txt2, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: color || C.txt, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: C.txt2, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ─── Table columns definition ─────────────────────────────────────────────────
const HOURS = [1,2,3,4,5,6,7,8];

// gridTemplateColumns: Line | Style | Buyer | SAM | Op | DB Eff | Hrly Tgt | H1..H8 | Total | Run Eff
const COLS = `80px 90px 80px 55px 40px 65px 65px repeat(8, 48px) 65px 70px`;

function TableHeader() {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: COLS,
      gap: 4, padding: '7px 10px',
      background: '#F0EDE8', borderBottom: `1px solid ${C.border}`,
      position: 'sticky', top: 0, zIndex: 1
    }}>
      {['Line','Style','Buyer','SAM','Op','DB Eff%','Hrly Tgt',...HOURS.map(h=>`H${h}`),'Total','Run Eff%'].map(h => (
        <div key={h} style={{ fontSize: 9, fontWeight: 700, color: C.txt2, textTransform: 'uppercase', textAlign: 'center' }}>{h}</div>
      ))}
    </div>
  );
}

function LineRow({ line, idx }) {
  const hourlyMap = {};
  (line.hourly || []).forEach(h => { hourlyMap[h.hour] = h.actual; });

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: COLS,
      gap: 4, padding: '7px 10px',
      background: idx % 2 === 0 ? '#fff' : '#FAFAF8',
      borderBottom: `0.5px solid ${C.border}`,
      alignItems: 'center'
    }}>
      {/* Line */}
      <div style={{ fontSize: 12, fontWeight: 700, color: C.txt }}>Line {line.line_number}</div>

      {/* Style */}
      <div style={{ fontSize: 11, color: C.txt, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={line.style_number}>{line.style_number}</div>

      {/* Buyer */}
      <div style={{ fontSize: 11, color: C.txt2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={line.buyer}>{line.buyer}</div>

      {/* SAM */}
      <div style={{ fontSize: 11, textAlign: 'center', color: C.txt }}>{parseFloat(line.total_sam || 0).toFixed(2)}</div>

      {/* Operators */}
      <div style={{ fontSize: 11, textAlign: 'center', fontWeight: 600, color: C.acc }}>{line.total_deployed}</div>

      {/* DB Efficiency */}
      <div style={{ textAlign: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 5px', borderRadius: 4, background: effBg(line.db_efficiency), color: effColor(line.db_efficiency) }}>
          {line.db_efficiency}%
        </span>
      </div>

      {/* Hourly target from line design */}
      <div style={{ fontSize: 11, textAlign: 'center', fontWeight: 600, color: C.txt }}>{line.target_per_hr}</div>

      {/* H1–H8 */}
      {HOURS.map(h => {
        const val = hourlyMap[h];
        const tgt = line.target_per_hr || 0;
        const isPast = h < line.current_hour;
        const isCur = h === line.current_hour;
        return (
          <div key={h} style={{
            textAlign: 'center', fontSize: 11, fontWeight: val != null ? 600 : 400,
            color: val == null ? C.txt2 : val >= tgt ? C.grn : C.red,
            background: isCur ? C.accBg : 'transparent',
            borderRadius: 3, padding: '1px 0'
          }}>
            {val != null ? val : (isPast ? '—' : '')}
          </div>
        );
      })}

      {/* Total output */}
      <div style={{ textAlign: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: line.total_output >= line.target_by_now ? C.grn : C.red }}>
          {line.total_output}
        </span>
      </div>

      {/* Running efficiency */}
      <div style={{ textAlign: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 5px', borderRadius: 4, background: effBg(line.running_efficiency), color: effColor(line.running_efficiency) }}>
          {line.running_efficiency}%
        </span>
      </div>
    </div>
  );
}

// ─── Compact per-line card for narrow (phone) screens ─────────────────────────
function LineCard({ line, idx }) {
  const hourlyMap = {};
  (line.hourly || []).forEach(h => { hourlyMap[h.hour] = h.actual; });

  return (
    <div style={{
      padding: '12px 14px', background: idx % 2 === 0 ? '#fff' : '#FAFAF8',
      borderBottom: `0.5px solid ${C.border}`
    }}>
      {/* Line / Style / Buyer */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: C.txt }}>Line {line.line_number}</div>
        <div style={{ fontSize: 11, color: C.txt2 }}>{line.style_number} · {line.buyer}</div>
      </div>

      {/* Compact stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 8.5, fontWeight: 600, color: C.txt2, textTransform: 'uppercase' }}>SAM</div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{parseFloat(line.total_sam || 0).toFixed(2)}</div>
        </div>
        <div>
          <div style={{ fontSize: 8.5, fontWeight: 600, color: C.txt2, textTransform: 'uppercase' }}>Ops</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.acc }}>{line.total_deployed}</div>
        </div>
        <div>
          <div style={{ fontSize: 8.5, fontWeight: 600, color: C.txt2, textTransform: 'uppercase' }}>DB Eff</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: effColor(line.db_efficiency) }}>{line.db_efficiency}%</div>
        </div>
        <div>
          <div style={{ fontSize: 8.5, fontWeight: 600, color: C.txt2, textTransform: 'uppercase' }}>Tgt/hr</div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{line.target_per_hr}</div>
        </div>
      </div>

      {/* Total + Running efficiency — the headline numbers */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
        <div style={{ flex: 1, background: line.total_output >= line.target_by_now ? C.grnBg : C.redBg, borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: 8.5, fontWeight: 600, color: C.txt2, textTransform: 'uppercase' }}>Total output</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: line.total_output >= line.target_by_now ? C.grn : C.red }}>{line.total_output}</div>
        </div>
        <div style={{ flex: 1, background: effBg(line.running_efficiency), borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: 8.5, fontWeight: 600, color: C.txt2, textTransform: 'uppercase' }}>Running eff</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: effColor(line.running_efficiency) }}>{line.running_efficiency}%</div>
        </div>
      </div>

      {/* Hourly breakdown — its own contained horizontal scroll, not page-wide */}
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ display: 'flex', gap: 4, minWidth: 'max-content' }}>
          {HOURS.map(h => {
            const val = hourlyMap[h];
            const tgt = line.target_per_hr || 0;
            const isPast = h < line.current_hour;
            const isCur = h === line.current_hour;
            return (
              <div key={h} style={{
                minWidth: 34, textAlign: 'center', borderRadius: 5, padding: '4px 2px',
                background: isCur ? C.accBg : '#F2F0EB'
              }}>
                <div style={{ fontSize: 8, color: C.txt2, fontWeight: 600 }}>H{h}</div>
                <div style={{
                  fontSize: 11, fontWeight: val != null ? 700 : 400,
                  color: val == null ? C.txt2 : val >= tgt ? C.grn : C.red
                }}>
                  {val != null ? val : (isPast ? '—' : '')}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FooterRow({ us, lines }) {
  const totalOutput = lines.reduce((a, l) => a + l.total_output, 0);
  const hourTotals = {};
  HOURS.forEach(h => {
    hourTotals[h] = lines.reduce((sum, l) => {
      const hEntry = (l.hourly || []).find(x => x.hour === h);
      return sum + (hEntry?.actual || 0);
    }, 0);
  });

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: COLS,
      gap: 4, padding: '7px 10px',
      background: '#E8E5E0', borderTop: `1px solid ${C.border}`,
      alignItems: 'center'
    }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: C.txt }}>TOTAL</div>
      <div /><div /><div /><div />
      <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: effColor(us.avg_db_efficiency) }}>{us.avg_db_efficiency}%</div>
      <div />
      {HOURS.map(h => (
        <div key={h} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: C.txt }}>
          {hourTotals[h] > 0 ? hourTotals[h] : ''}
        </div>
      ))}
      <div style={{ textAlign: 'center', fontSize: 12, fontWeight: 800, color: C.acc }}>{totalOutput}</div>
      <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: effColor(us.avg_efficiency) }}>{us.avg_efficiency}%</div>
    </div>
  );
}

// ─── Compact footer summary for narrow (phone) screens ────────────────────────
function FooterCard({ us, lines }) {
  const totalOutput = lines.reduce((a, l) => a + l.total_output, 0);
  return (
    <div style={{ padding: '12px 14px', background: '#E8E5E0' }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: C.txt, marginBottom: 8 }}>UNIT TOTAL</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 8.5, fontWeight: 600, color: C.txt2, textTransform: 'uppercase' }}>DB Eff</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: effColor(us.avg_db_efficiency) }}>{us.avg_db_efficiency}%</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 8.5, fontWeight: 600, color: C.txt2, textTransform: 'uppercase' }}>Output</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.acc }}>{totalOutput}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 8.5, fontWeight: 600, color: C.txt2, textTransform: 'uppercase' }}>Running eff</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: effColor(us.avg_efficiency) }}>{us.avg_efficiency}%</div>
        </div>
      </div>
    </div>
  );
}

export default function PlantHeadDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [factories, setFactories] = useState([]);
  const [selectedFactory, setSelectedFactory] = useState('');
  const [selectedShift, setSelectedShift] = useState('');
  const SHIFT_OPTIONS = ['General', 'First', 'Second', 'Third'];
  const [isNarrow, setIsNarrow] = useState(typeof window !== 'undefined' ? window.innerWidth < 760 : false);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 760);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const el = document.getElementById('lc-page-title');
    if (el) el.textContent = 'Plant head dashboard';
  }, []);

  useEffect(() => {
    fetch(`${API}/factories`, { headers: authHeaders() })
      .then(r => r.json()).then(d => { if (Array.isArray(d)) setFactories(d); }).catch(() => {});
  }, []);

  const loadData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedFactory) params.append('factory_id', selectedFactory);
    if (selectedShift) params.append('shift_name', selectedShift);
    const qs = params.toString();
    const url = qs ? `${API}/plant-dashboard?${qs}` : `${API}/plant-dashboard`;
    fetch(url, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        if (!d.error) { setData(d); setLastRefresh(new Date()); }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [selectedFactory, selectedShift]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  const now = new Date();
  const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
  const dateStr = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const { lines = [], unit_summary: us } = data || {};

  return (
    <div style={{ background: C.bg, minHeight: '100vh', padding: 16, fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>

      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.acc }}>Skillflo</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.txt }}>Plant Head Dashboard</div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 12, color: C.txt2 }}>
            {dateStr} · <span style={{ fontWeight: 700, color: C.txt }}>{timeStr}</span>
          </div>
          {lastRefresh && !isNarrow && (
            <div style={{ fontSize: 10, color: C.txt2 }}>
              Refreshed: {lastRefresh.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
            </div>
          )}
          <select value={selectedFactory} onChange={e => setSelectedFactory(e.target.value)}
            style={{ padding: '4px 10px', borderRadius: 6, border: `0.5px solid ${C.border}`, background: C.card, fontSize: 11, color: C.txt, cursor: 'pointer' }}>
            <option value="">All factories</option>
            {factories.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <select value={selectedShift} onChange={e => setSelectedShift(e.target.value)}
            style={{ padding: '4px 10px', borderRadius: 6, border: `0.5px solid ${C.border}`, background: C.card, fontSize: 11, color: C.txt, cursor: 'pointer' }}>
            <option value="">All shifts</option>
            {SHIFT_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={loadData} style={{ padding: '4px 10px', borderRadius: 6, border: `0.5px solid ${C.border}`, background: C.card, fontSize: 11, cursor: 'pointer', color: C.acc, fontWeight: 600 }}>↺ Refresh</button>
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 60, color: C.txt2, fontSize: 13 }}>Loading…</div>}

      {!loading && lines.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: C.txt2, fontSize: 14 }}>
          No active deployments. Activate a deployment first.
        </div>
      )}

      {!loading && lines.length > 0 && us && (
        <>
          {/* Unit KPI strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 12 }}>
            <UnitKpi label="Active lines" value={us.total_lines} color={C.acc} />
            <UnitKpi label="Unit DB Efficiency" value={`${us.avg_db_efficiency}%`} sub="Deployment plan avg" color={effColor(us.avg_db_efficiency)} />
            <UnitKpi label="Running Efficiency" value={`${us.avg_efficiency}%`} sub="Actual vs target" color={effColor(us.avg_efficiency)} />
            <UnitKpi label="Unit output" value={us.total_output} sub={`Target: ${us.total_target}`} color={us.total_output >= us.total_target ? C.grn : C.red} />
            <UnitKpi label="Operators" value={us.total_deployed} sub={`of ${us.total_manpower} scheduled`} color={C.acc} />
            <UnitKpi label="Skill gaps" value={us.total_skill_gaps} color={us.total_skill_gaps > 0 ? C.amb : C.grn} />
            <UnitKpi label="Active DT" value={us.total_active_dt} color={us.total_active_dt > 0 ? C.red : C.grn} />
          </div>

          {/* Main table — dense grid on wide screens (TV/desktop), stacked cards on phones */}
          <div style={{ background: C.card, borderRadius: 10, border: `0.5px solid ${C.border}`, overflow: 'hidden' }}>
            {isNarrow ? (
              <>
                <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 220px)' }}>
                  {lines.map((line, i) => <LineCard key={line.deployment_id} line={line} idx={i} />)}
                </div>
                <FooterCard us={us} lines={lines} />
              </>
            ) : (
              <>
                <TableHeader />
                <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 260px)', overflowX: 'auto' }}>
                  {lines.map((line, i) => <LineRow key={line.deployment_id} line={line} idx={i} />)}
                </div>
                <FooterRow us={us} lines={lines} />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}